import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  FiPercent,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiSave,
  FiClock,
  FiTrendingUp,
  FiRefreshCw,
  FiUpload,
  FiCheck,
  FiX,
} from "react-icons/fi";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CardSkeleton } from "@/components/common";
import * as XLSX from "xlsx";
import { getLenderLogo } from "@/config/lender-logos.config";

// Types matching database schema
interface Lender {
  id: string;
  name: string;
  logoUrl: string | null;
  website: string | null;
  isActive: boolean;
}

interface Rate {
  id: string;
  lenderId: string;
  productName: string;
  rateType: "variable" | "fixed_1yr" | "fixed_2yr" | "fixed_3yr" | "fixed_5yr";
  loanType: "owner_occupied" | "investment" | null;
  interestRate: number;
  comparisonRate: number | null;
  lvrMax: number | null;
  features: string | null;
  isActive: boolean;
}

// Combined view for display
interface LenderWithRates extends Lender {
  rates: {
    variable?: Rate;
    fixed1yr?: Rate;
    fixed2yr?: Rate;
    fixed3yr?: Rate;
    fixed5yr?: Rate;
  };
}

interface EditFormData {
  name: string;
  logoUrl: string;
  variable: number;
  comparison: number;
  fixed1yr: number;
  fixed2yr: number;
  fixed3yr: number;
  fixed5yr: number;
  maxLVR: number;
  loanType: "owner_occupied" | "investment" | "both";
  features: string;
}

const emptyFormData: EditFormData = {
  name: "",
  logoUrl: "",
  variable: 0,
  comparison: 0,
  fixed1yr: 0,
  fixed2yr: 0,
  fixed3yr: 0,
  fixed5yr: 0,
  maxLVR: 80,
  loanType: "both",
  features: "",
};

// Types for Excel import
interface ImportedLenderRate {
  lenderName: string;
  productName: string;
  productType: string;
  rate: number;
  maxLvrOO: number | null;
  maxLvrInv: number | null;
  isFixed: boolean;
  fixedTerm?: string; // 1yr, 2yr, 3yr, 5yr
}

interface AggregatedLenderData {
  name: string;
  variable: number;
  fixed1yr: number;
  fixed2yr: number;
  fixed3yr: number;
  fixed5yr: number;
  maxLVR: number;
  productCount: number;
}

// Helper to parse fixed term from product name
function parseFixedTerm(productName: string): string | null {
  const lowerName = productName.toLowerCase();
  if (lowerName.includes("1 year") || lowerName.includes("1yr") || lowerName.includes("1 yr")) return "1yr";
  if (lowerName.includes("2 year") || lowerName.includes("2yr") || lowerName.includes("2 yr")) return "2yr";
  if (lowerName.includes("3 year") || lowerName.includes("3yr") || lowerName.includes("3 yr")) return "3yr";
  if (lowerName.includes("5 year") || lowerName.includes("5yr") || lowerName.includes("5 yr")) return "5yr";
  return null;
}

// Helper to aggregate rates by lender
function aggregateLenderRates(rates: ImportedLenderRate[]): AggregatedLenderData[] {
  const lenderMap = new Map<string, AggregatedLenderData>();

  for (const rate of rates) {
    const existing = lenderMap.get(rate.lenderName) || {
      name: rate.lenderName,
      variable: 0,
      fixed1yr: 0,
      fixed2yr: 0,
      fixed3yr: 0,
      fixed5yr: 0,
      maxLVR: 0,
      productCount: 0,
    };

    // Update with lowest rate for each type
    if (!rate.isFixed && rate.productType !== "Intro") {
      if (existing.variable === 0 || rate.rate < existing.variable) {
        existing.variable = rate.rate;
      }
    } else if (rate.isFixed) {
      const term = rate.fixedTerm || parseFixedTerm(rate.productName);
      if (term === "1yr" && (existing.fixed1yr === 0 || rate.rate < existing.fixed1yr)) {
        existing.fixed1yr = rate.rate;
      } else if (term === "2yr" && (existing.fixed2yr === 0 || rate.rate < existing.fixed2yr)) {
        existing.fixed2yr = rate.rate;
      } else if (term === "3yr" && (existing.fixed3yr === 0 || rate.rate < existing.fixed3yr)) {
        existing.fixed3yr = rate.rate;
      } else if (term === "5yr" && (existing.fixed5yr === 0 || rate.rate < existing.fixed5yr)) {
        existing.fixed5yr = rate.rate;
      }
    }

    // Update max LVR
    const lvr = Math.max(rate.maxLvrOO || 0, rate.maxLvrInv || 0);
    if (lvr > existing.maxLVR) {
      existing.maxLVR = lvr;
    }

    existing.productCount++;
    lenderMap.set(rate.lenderName, existing);
  }

  return Array.from(lenderMap.values())
    .filter(l => l.variable > 0 || l.fixed1yr > 0 || l.fixed2yr > 0)
    .sort((a, b) => {
      // Sort by variable rate, then by name
      if (a.variable && b.variable) return a.variable - b.variable;
      if (a.variable) return -1;
      if (b.variable) return 1;
      return a.name.localeCompare(b.name);
    });
}

export function RatesManagerPage() {
  const [lenders, setLenders] = useState<Lender[]>([]);
  const [rates, setRates] = useState<Rate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingLender, setEditingLender] = useState<Lender | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isNewLender, setIsNewLender] = useState(false);
  const [formData, setFormData] = useState<EditFormData>(emptyFormData);

  // Import state
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [importedData, setImportedData] = useState<AggregatedLenderData[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [importFileName, setImportFileName] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch lenders and rates
  const fetchData = useCallback(async () => {
    try {
      const [lendersRes, ratesRes] = await Promise.all([
        fetch("/api/admin/lenders", { credentials: "include" }),
        fetch("/api/admin/rates", { credentials: "include" }),
      ]);

      if (lendersRes.ok && ratesRes.ok) {
        const lendersData = await lendersRes.json() as Lender[];
        const ratesData = await ratesRes.json() as Rate[];
        setLenders(lendersData);
        setRates(ratesData);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load rates data");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Process Excel file
  const processExcelFile = async (file: File) => {
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }

    setImportFileName(file.name);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);

      const allRates: ImportedLenderRate[] = [];

      // Helper to parse sheet with standard format: Lender Name, Product Name, Rate, Min, Max, LVR OO, LVR INV, LVR LAND
      const parseProductSheet = (sheetName: string, isFixed: boolean, fixedTerm?: string) => {
        const sheet = workbook.Sheets[sheetName];
        if (!sheet) return;

        const sheetData = XLSX.utils.sheet_to_json<(string | number | null)[]>(sheet, { header: 1 });

        for (let i = 2; i < sheetData.length; i++) {
          const row = sheetData[i];
          if (!row || !row[0] || row[2] === undefined || row[2] === null) continue;

          const rate = typeof row[2] === "number" ? row[2] : parseFloat(String(row[2]));
          if (isNaN(rate) || rate <= 0 || rate >= 1) continue;

          const maxLvrOO = row[5] ? (typeof row[5] === "number" ? row[5] * 100 : parseFloat(String(row[5])) * 100) : null;
          const maxLvrInv = row[6] ? (typeof row[6] === "number" ? row[6] * 100 : parseFloat(String(row[6])) * 100) : null;

          allRates.push({
            lenderName: String(row[0]).trim(),
            productName: String(row[1] || "").trim(),
            productType: isFixed ? `Fixed ${fixedTerm || ""}` : "Variable",
            rate: rate * 100,
            maxLvrOO: maxLvrOO && !isNaN(maxLvrOO) ? maxLvrOO : null,
            maxLvrInv: maxLvrInv && !isNaN(maxLvrInv) ? maxLvrInv : null,
            isFixed,
            fixedTerm,
          });
        }
      };

      // Parse variable rate sheets
      parseProductSheet("Std Variable Products", false);
      parseProductSheet("Basic Products", false);

      // Parse fixed rate sheet - need to detect term from product name
      const fixedSheet = workbook.Sheets["Fixed Products"];
      if (fixedSheet) {
        const fixedData = XLSX.utils.sheet_to_json<(string | number | null)[]>(fixedSheet, { header: 1 });

        for (let i = 2; i < fixedData.length; i++) {
          const row = fixedData[i];
          if (!row || !row[0] || row[2] === undefined || row[2] === null) continue;

          const rate = typeof row[2] === "number" ? row[2] : parseFloat(String(row[2]));
          if (isNaN(rate) || rate <= 0 || rate >= 1) continue;

          const productName = String(row[1] || "").trim();
          const maxLvrOO = row[5] ? (typeof row[5] === "number" ? row[5] * 100 : parseFloat(String(row[5])) * 100) : null;
          const maxLvrInv = row[6] ? (typeof row[6] === "number" ? row[6] * 100 : parseFloat(String(row[6])) * 100) : null;

          allRates.push({
            lenderName: String(row[0]).trim(),
            productName,
            productType: "Fixed",
            rate: rate * 100,
            maxLvrOO: maxLvrOO && !isNaN(maxLvrOO) ? maxLvrOO : null,
            maxLvrInv: maxLvrInv && !isNaN(maxLvrInv) ? maxLvrInv : null,
            isFixed: true,
            fixedTerm: parseFixedTerm(productName) || undefined,
          });
        }
      }

      // Aggregate by lender (normalize names)
      const aggregated = aggregateLenderRates(allRates);
      setImportedData(aggregated);
      setIsImportDialogOpen(true);

      toast.success(`Parsed ${allRates.length} rates from ${aggregated.length} lenders`);
    } catch (error) {
      console.error("Failed to parse Excel:", error);
      toast.error("Failed to parse Excel file");
    }
  };

  // Handle file input change
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await processExcelFile(file);
    }
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      await processExcelFile(file);
    }
  };

  // Handle import confirmation
  const handleImportConfirm = async () => {
    if (importedData.length === 0) return;

    setIsImporting(true);

    try {
      // Import each lender
      let successCount = 0;
      let errorCount = 0;
      const errors: string[] = [];

      for (const lenderData of importedData) {
        try {
          // Check if lender exists (case-insensitive match)
          const existingLender = lenders.find(
            l => l.name.toLowerCase().trim() === lenderData.name.toLowerCase().trim()
          );

          let lenderId: string;

          if (existingLender) {
            lenderId = existingLender.id;

            // Update logo if missing
            if (!existingLender.logoUrl) {
              const logoUrl = getLenderLogo(lenderData.name);
              if (logoUrl) {
                await fetch(`/api/admin/lenders/${lenderId}`, {
                  method: "PUT",
                  credentials: "include",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ logoUrl }),
                });
              }
            }
          } else {
            // Create new lender with auto-mapped logo
            const logoUrl = getLenderLogo(lenderData.name);
            const res = await fetch("/api/admin/lenders", {
              method: "POST",
              credentials: "include",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                name: lenderData.name.trim(),
                logoUrl: logoUrl || null,
                isActive: true,
              }),
            });

            if (!res.ok) {
              const errText = await res.text();
              errors.push(`${lenderData.name}: Failed to create lender - ${errText}`);
              errorCount++;
              continue;
            }

            const responseData = await res.json() as { success?: boolean; data?: Lender; id?: string };
            // Handle response format: { success: true, data: { id: "...", ... } }
            lenderId = responseData.data?.id || responseData.id || "";

            if (!lenderId) {
              errors.push(`${lenderData.name}: No lender ID returned. Response: ${JSON.stringify(responseData)}`);
              errorCount++;
              continue;
            }

            // Add to local lenders list to avoid duplicate creation attempts
            lenders.push({ id: lenderId, name: lenderData.name, logoUrl: null, website: null, isActive: true });
          }

          // Update rates - only if we have valid rates
          if (lenderData.variable > 0 || lenderData.fixed1yr > 0 || lenderData.fixed2yr > 0) {
            const ratesRes = await fetch(`/api/admin/lenders/${lenderId}/rates`, {
              method: "POST",
              credentials: "include",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                variable: lenderData.variable || 0,
                fixed1yr: lenderData.fixed1yr || 0,
                fixed2yr: lenderData.fixed2yr || 0,
                fixed3yr: lenderData.fixed3yr || 0,
                fixed5yr: lenderData.fixed5yr || 0,
                comparisonRate: 0,
                maxLVR: lenderData.maxLVR || 80,
                loanType: null,
                features: [],
              }),
            });

            if (ratesRes.ok) {
              successCount++;
            } else {
              const errText = await ratesRes.text();
              errors.push(`${lenderData.name}: Failed to save rates - ${errText}`);
              errorCount++;
            }
          } else {
            // Skip lenders with no rates
            successCount++;
          }
        } catch (err) {
          errors.push(`${lenderData.name}: ${err instanceof Error ? err.message : "Unknown error"}`);
          errorCount++;
        }
      }

      if (errorCount > 0) {
        console.error("Import errors:", errors);
        toast.error(`Import complete with errors: ${successCount} success, ${errorCount} failed. Check console for details.`);
      } else {
        toast.success(`Import complete: ${successCount} lenders updated successfully`);
      }

      setIsImportDialogOpen(false);
      setImportedData([]);
      fetchData();
    } catch (error) {
      toast.error("Import failed: " + (error instanceof Error ? error.message : "Unknown error"));
    } finally {
      setIsImporting(false);
    }
  };

  // Combine lenders with their rates for display
  const lendersWithRates: LenderWithRates[] = lenders.map((lender) => {
    const lenderRates = rates.filter((r) => r.lenderId === lender.id);
    return {
      ...lender,
      rates: {
        variable: lenderRates.find((r) => r.rateType === "variable"),
        fixed1yr: lenderRates.find((r) => r.rateType === "fixed_1yr"),
        fixed2yr: lenderRates.find((r) => r.rateType === "fixed_2yr"),
        fixed3yr: lenderRates.find((r) => r.rateType === "fixed_3yr"),
        fixed5yr: lenderRates.find((r) => r.rateType === "fixed_5yr"),
      },
    };
  });

  // Handle edit click
  const handleEdit = (lender: LenderWithRates) => {
    setEditingLender(lender);
    setIsNewLender(false);
    setFormData({
      name: lender.name,
      logoUrl: lender.logoUrl || "",
      variable: lender.rates.variable?.interestRate || 0,
      comparison: lender.rates.variable?.comparisonRate || 0,
      fixed1yr: lender.rates.fixed1yr?.interestRate || 0,
      fixed2yr: lender.rates.fixed2yr?.interestRate || 0,
      fixed3yr: lender.rates.fixed3yr?.interestRate || 0,
      fixed5yr: lender.rates.fixed5yr?.interestRate || 0,
      maxLVR: lender.rates.variable?.lvrMax || 80,
      loanType: lender.rates.variable?.loanType === "owner_occupied"
        ? "owner_occupied"
        : lender.rates.variable?.loanType === "investment"
          ? "investment"
          : "both",
      features: lender.rates.variable?.features
        ? JSON.parse(lender.rates.variable.features).join(", ")
        : "",
    });
    setIsDialogOpen(true);
  };

  // Handle add new
  const handleAddNew = () => {
    setEditingLender(null);
    setIsNewLender(true);
    setFormData(emptyFormData);
    setIsDialogOpen(true);
  };

  // Auto-suggest logo when name changes
  const handleNameChange = (name: string) => {
    const suggestedLogo = getLenderLogo(name);
    setFormData((prev) => ({
      ...prev,
      name,
      logoUrl: prev.logoUrl || suggestedLogo || "",
    }));
  };

  // Handle save
  const handleSave = async () => {
    if (!formData.name) {
      toast.error("Lender name is required");
      return;
    }

    setIsSubmitting(true);

    try {
      let lenderId: string;

      if (isNewLender) {
        // Create new lender
        const res = await fetch("/api/admin/lenders", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: formData.name,
            logoUrl: formData.logoUrl || null,
            isActive: true,
          }),
        });

        if (!res.ok) throw new Error("Failed to create lender");

        const data = await res.json() as { data: Lender };
        lenderId = data.data.id;
      } else if (editingLender) {
        // Update existing lender
        const res = await fetch(`/api/admin/lenders/${editingLender.id}`, {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: formData.name,
            logoUrl: formData.logoUrl || null,
            isActive: true,
          }),
        });

        if (!res.ok) throw new Error("Failed to update lender");

        lenderId = editingLender.id;
      } else {
        throw new Error("No lender to save");
      }

      // Save rates using bulk upsert
      const features = formData.features
        .split(",")
        .map((f) => f.trim())
        .filter((f) => f.length > 0);

      const ratesRes = await fetch(`/api/admin/lenders/${lenderId}/rates`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          variable: formData.variable,
          fixed1yr: formData.fixed1yr,
          fixed2yr: formData.fixed2yr,
          fixed3yr: formData.fixed3yr,
          fixed5yr: formData.fixed5yr,
          comparisonRate: formData.comparison,
          maxLVR: formData.maxLVR,
          loanType: formData.loanType === "both" ? null : formData.loanType,
          features,
        }),
      });

      if (!ratesRes.ok) throw new Error("Failed to save rates");

      toast.success(isNewLender ? "Lender added successfully" : "Lender updated successfully");
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to save");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete
  const handleDelete = async (lender: Lender) => {
    if (!confirm(`Are you sure you want to delete ${lender.name}? This will also delete all associated rates.`)) {
      return;
    }

    try {
      const res = await fetch(`/api/admin/lenders/${lender.id}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!res.ok) throw new Error("Failed to delete lender");

      toast.success("Lender deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete lender");
    }
  };

  // Calculate stats
  const activeRates = rates.filter((r) => r.isActive);
  const variableRates = activeRates.filter((r) => r.rateType === "variable");
  const lowestVariable = variableRates.length > 0
    ? Math.min(...variableRates.map((r) => r.interestRate))
    : 0;

  const fixedRates = activeRates.filter((r) => r.rateType.startsWith("fixed"));
  const lowestFixed = fixedRates.length > 0
    ? Math.min(...fixedRates.map((r) => r.interestRate))
    : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-medium text-slate-900">
            Manage Rates
          </h1>
          <p className="text-muted-foreground mt-1">
            Update lender rates - changes apply to public /rates page
          </p>
        </div>

        <div className="flex gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileSelect}
            className="hidden"
          />
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
          >
            <FiUpload className="mr-2 h-4 w-4" />
            Import Excel
          </Button>
          <Button variant="outline" onClick={fetchData} disabled={isLoading}>
            <FiRefreshCw className={`mr-2 h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button className="btn-pill" onClick={handleAddNew}>
            <FiPlus className="mr-2 h-4 w-4" />
            Add Lender
          </Button>
        </div>
      </div>

      {/* Drop Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`
          border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all
          ${isDragging
            ? "border-primary bg-primary/5 scale-[1.02]"
            : "border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/50"
          }
        `}
      >
        <FiUpload className={`mx-auto h-8 w-8 mb-2 ${isDragging ? "text-primary" : "text-muted-foreground"}`} />
        <p className={`font-medium ${isDragging ? "text-primary" : "text-foreground"}`}>
          {isDragging ? "Drop Excel file here" : "Drag & drop AFG rates Excel file here"}
        </p>
        <p className="text-sm text-muted-foreground mt-1">
          or click to browse • Supports .xlsx and .xls files
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <FiPercent className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Lowest Variable</p>
                <p className="text-2xl font-bold">
                  {lowestVariable > 0 ? `${lowestVariable.toFixed(2)}%` : "-"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <FiPercent className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Lowest Fixed</p>
                <p className="text-2xl font-bold">
                  {lowestFixed > 0 ? `${lowestFixed.toFixed(2)}%` : "-"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <FiTrendingUp className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Lenders</p>
                <p className="text-2xl font-bold">{lenders.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <FiClock className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Rates</p>
                <p className="text-2xl font-bold">{rates.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lenders Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FiPercent className="w-5 h-5" />
            Lender Rates
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              <CardSkeleton />
              <CardSkeleton />
              <CardSkeleton />
            </div>
          ) : lendersWithRates.length === 0 ? (
            <div className="text-center py-8">
              <FiPercent className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">No lenders yet</p>
              <Button className="mt-4 btn-pill" onClick={handleAddNew}>
                <FiPlus className="mr-2 h-4 w-4" />
                Add your first lender
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-3 font-medium text-sm">Lender</th>
                    <th className="pb-3 font-medium text-sm text-center">Variable</th>
                    <th className="pb-3 font-medium text-sm text-center">Comparison</th>
                    <th className="pb-3 font-medium text-sm text-center">1yr Fixed</th>
                    <th className="pb-3 font-medium text-sm text-center">2yr Fixed</th>
                    <th className="pb-3 font-medium text-sm text-center">Max LVR</th>
                    <th className="pb-3 font-medium text-sm text-center">Status</th>
                    <th className="pb-3 font-medium text-sm text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {lendersWithRates.map((lender) => {
                    // Use database logoUrl or fall back to config lookup
                    const logoUrl = lender.logoUrl || getLenderLogo(lender.name);
                    const showLogo = logoUrl && !logoUrl.includes("unnamed.png");
                    return (
                    <tr
                      key={lender.id}
                      className="border-b last:border-0 hover:bg-muted/50"
                    >
                      <td className="py-3">
                        <div className="flex items-center gap-3">
                          {showLogo && (
                            <img
                              src={logoUrl}
                              alt={lender.name}
                              className="h-8 w-auto max-w-[100px] object-contain"
                              onError={(e) => (e.currentTarget.style.display = "none")}
                            />
                          )}
                          <span className="font-medium text-sm">{lender.name}</span>
                        </div>
                      </td>
                      <td className="py-3 text-center">
                        <span
                          className={
                            lender.rates.variable?.interestRate === lowestVariable
                              ? "text-green-600 font-bold"
                              : ""
                          }
                        >
                          {lender.rates.variable?.interestRate.toFixed(2) || "-"}%
                        </span>
                      </td>
                      <td className="py-3 text-center text-muted-foreground">
                        {lender.rates.variable?.comparisonRate?.toFixed(2) || "-"}%
                      </td>
                      <td className="py-3 text-center">
                        {lender.rates.fixed1yr?.interestRate.toFixed(2) || "-"}%
                      </td>
                      <td className="py-3 text-center">
                        {lender.rates.fixed2yr?.interestRate.toFixed(2) || "-"}%
                      </td>
                      <td className="py-3 text-center">
                        {lender.rates.variable?.lvrMax || "-"}%
                      </td>
                      <td className="py-3 text-center">
                        <Badge
                          variant="secondary"
                          className={
                            lender.isActive
                              ? "bg-green-100 text-green-700"
                              : "bg-gray-100 text-gray-500"
                          }
                        >
                          {lender.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </td>
                      <td className="py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(lender)}
                          >
                            <FiEdit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(lender)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <FiTrash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {isNewLender ? "Add New Lender" : `Edit ${editingLender?.name}`}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Lender Name *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="Commonwealth Bank"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Logo URL</label>
                <div className="flex gap-2">
                  <Input
                    value={formData.logoUrl}
                    onChange={(e) =>
                      setFormData({ ...formData, logoUrl: e.target.value })
                    }
                    placeholder="/lenders/bank.png"
                    className="flex-1"
                  />
                  {formData.logoUrl && (
                    <div className="w-12 h-9 border rounded flex items-center justify-center bg-white">
                      <img
                        src={formData.logoUrl}
                        alt="Logo preview"
                        className="max-h-6 max-w-[40px] object-contain"
                        onError={(e) => (e.currentTarget.style.display = "none")}
                      />
                    </div>
                  )}
                </div>
                {!formData.logoUrl && formData.name && (
                  <p className="text-xs text-muted-foreground mt-1">
                    No logo mapped for this lender name
                  </p>
                )}
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Interest Rates</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium">Variable Rate *</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.variable || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        variable: parseFloat(e.target.value) || 0,
                      })
                    }
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Comparison Rate</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.comparison || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        comparison: parseFloat(e.target.value) || 0,
                      })
                    }
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Max LVR (%)</label>
                  <Input
                    type="number"
                    value={formData.maxLVR || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        maxLVR: parseInt(e.target.value) || 0,
                      })
                    }
                  />
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Fixed Rates</h4>
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className="text-sm font-medium">1 Year</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.fixed1yr || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        fixed1yr: parseFloat(e.target.value) || 0,
                      })
                    }
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">2 Year</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.fixed2yr || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        fixed2yr: parseFloat(e.target.value) || 0,
                      })
                    }
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">3 Year</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.fixed3yr || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        fixed3yr: parseFloat(e.target.value) || 0,
                      })
                    }
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">5 Year</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.fixed5yr || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        fixed5yr: parseFloat(e.target.value) || 0,
                      })
                    }
                  />
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Additional Details</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Loan Type</label>
                  <select
                    value={formData.loanType}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        loanType: e.target.value as EditFormData["loanType"],
                      })
                    }
                    className="w-full h-9 px-3 border border-input rounded-md text-sm"
                  >
                    <option value="both">Owner & Investor</option>
                    <option value="owner_occupied">Owner Occupied Only</option>
                    <option value="investment">Investor Only</option>
                  </select>
                </div>
              </div>
              <div className="mt-4">
                <label className="text-sm font-medium">
                  Features (comma separated)
                </label>
                <Input
                  value={formData.features}
                  onChange={(e) =>
                    setFormData({ ...formData, features: e.target.value })
                  }
                  placeholder="Offset account, Redraw facility, Extra repayments"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setIsDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1"
                onClick={handleSave}
                disabled={isSubmitting}
              >
                <FiSave className="mr-2 h-4 w-4" />
                {isSubmitting
                  ? "Saving..."
                  : isNewLender
                    ? "Add Lender"
                    : "Save Changes"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Info Note */}
      <Card className="bg-green-50 border-green-200">
        <CardContent className="p-4">
          <p className="text-sm text-green-800">
            <strong>Connected to database!</strong> Changes made here will be saved to D1
            and immediately reflected on the public /rates page, /lenders page, and
            homepage rate displays.
          </p>
        </CardContent>
      </Card>

      {/* Import Preview Dialog */}
      <Dialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen}>
        <DialogContent className="!max-w-6xl !w-[95vw] max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FiUpload className="h-5 w-5" />
              Import Rates from Excel
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-hidden flex flex-col">
            {importFileName && (
              <p className="text-sm text-muted-foreground mb-4">
                File: <strong>{importFileName}</strong> • {importedData.length} lenders found
              </p>
            )}

            {/* Preview Table */}
            <div className="flex-1 overflow-auto border rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-muted sticky top-0">
                  <tr>
                    <th className="p-3 text-left font-medium min-w-[200px]">Lender</th>
                    <th className="p-3 text-center font-medium w-24">Variable</th>
                    <th className="p-3 text-center font-medium w-24">1yr Fixed</th>
                    <th className="p-3 text-center font-medium w-24">2yr Fixed</th>
                    <th className="p-3 text-center font-medium w-24">3yr Fixed</th>
                    <th className="p-3 text-center font-medium w-24">5yr Fixed</th>
                    <th className="p-3 text-center font-medium w-24">Max LVR</th>
                    <th className="p-3 text-center font-medium w-24">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {importedData.slice(0, 50).map((lender, i) => {
                    const existing = lenders.find(
                      l => l.name.toLowerCase() === lender.name.toLowerCase()
                    );
                    const logoUrl = getLenderLogo(lender.name);
                    return (
                      <tr key={i} className="border-t hover:bg-muted/50">
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            {logoUrl && (
                              <img
                                src={logoUrl}
                                alt={lender.name}
                                className="h-5 w-auto max-w-[60px] object-contain"
                                onError={(e) => (e.currentTarget.style.display = "none")}
                              />
                            )}
                            <span className="font-medium">{lender.name}</span>
                          </div>
                        </td>
                        <td className="p-3 text-center tabular-nums">
                          {lender.variable > 0 ? `${lender.variable.toFixed(2)}%` : "-"}
                        </td>
                        <td className="p-3 text-center tabular-nums">
                          {lender.fixed1yr > 0 ? `${lender.fixed1yr.toFixed(2)}%` : "-"}
                        </td>
                        <td className="p-3 text-center tabular-nums">
                          {lender.fixed2yr > 0 ? `${lender.fixed2yr.toFixed(2)}%` : "-"}
                        </td>
                        <td className="p-3 text-center tabular-nums">
                          {lender.fixed3yr > 0 ? `${lender.fixed3yr.toFixed(2)}%` : "-"}
                        </td>
                        <td className="p-3 text-center tabular-nums">
                          {lender.fixed5yr > 0 ? `${lender.fixed5yr.toFixed(2)}%` : "-"}
                        </td>
                        <td className="p-3 text-center tabular-nums">
                          {lender.maxLVR > 0 ? `${lender.maxLVR.toFixed(0)}%` : "-"}
                        </td>
                        <td className="p-3 text-center">
                          <Badge
                            variant="secondary"
                            className={existing ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"}
                          >
                            {existing ? "Update" : "New"}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {importedData.length > 50 && (
                <p className="p-2 text-center text-sm text-muted-foreground bg-muted">
                  ... and {importedData.length - 50} more lenders
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between pt-4 border-t mt-4">
              <p className="text-sm text-muted-foreground">
                {importedData.filter(l => lenders.some(el => el.name.toLowerCase() === l.name.toLowerCase())).length} existing lenders will be updated,{" "}
                {importedData.filter(l => !lenders.some(el => el.name.toLowerCase() === l.name.toLowerCase())).length} new lenders will be created
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsImportDialogOpen(false);
                    setImportedData([]);
                  }}
                  disabled={isImporting}
                >
                  <FiX className="mr-2 h-4 w-4" />
                  Cancel
                </Button>
                <Button
                  onClick={handleImportConfirm}
                  disabled={isImporting || importedData.length === 0}
                >
                  {isImporting ? (
                    <>
                      <FiRefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <FiCheck className="mr-2 h-4 w-4" />
                      Import {importedData.length} Lenders
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
